import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import { adminApi } from '../../services/adminApi';
import '../../styles/BrandsOnboarding.css';

function BrandsOnboarding({ email, onLogout }) {
  const [brands, setBrands] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'partner' | 'candidates' | 'all'

  // Excel Column Filters & Sorting State
  const [columnFilters, setColumnFilters] = useState({}); // { [colKey]: Array<string> }
  const [rangeFilters, setRangeFilters] = useState({
    products: { min: '', max: '' },
    return_days: { min: '', max: '' },
    shipping_days: { min: '', max: '' },
  }); // { [colKey]: { min: string, max: string } }
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); // { key: string|null, direction: 'asc'|'desc' }

  const [activeFilterCol, setActiveFilterCol] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [valSearch, setValSearch] = useState('');
  const popoverRef = useRef(null);

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [togglingBrandId, setTogglingBrandId] = useState(null);

  // Close filter popover on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        if (!e.target.closest('.th-filter-btn')) {
          setActiveFilterCol(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Forms
  const [newBrandForm, setNewBrandForm] = useState({
    brand_name: '',
    storefront_domain: '',
    founder_name: '',
    founder_phone: '',
    founder_email: '',
    founder_social: '',
    return_days: '',
    shipping_days: '',
    email_sent: false,
    custom_fields: {},
  });

  const [editBrandForm, setEditBrandForm] = useState({
    brand_name: '',
    storefront_domain: '',
    founder_name: '',
    founder_phone: '',
    founder_email: '',
    founder_social: '',
    email_sent: false,
    custom_fields: {},
  });

  const [newFieldKey, setNewFieldKey] = useState('');



  const fetchCustomFields = async () => {
    try {
      const data = await adminApi.getCustomFields();
      setCustomFieldKeys(data.keys || []);
    } catch (err) {
      console.warn('Could not fetch custom fields:', err);
    }
  };

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getBrands({ limit: 2000 });
      setBrands(data.brands || data || []);
      if (data.custom_field_keys) {
        setCustomFieldKeys(data.custom_field_keys);
      }
    } catch (err) {
      console.error('Failed to load brands:', err);
      setError(err.message || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomFields();
    fetchBrands();
  }, [fetchBrands]);

  // eslint-disable-next-line no-unused-vars
  const handleToggleEmailSent = async (brand, e) => {
    e.stopPropagation();
    try {
      const id = brand.id || brand._id;
      const nextVal = !brand.email_sent;
      const updated = await adminApi.updateBrand(id, { email_sent: nextVal });
      setBrands((prev) =>
        prev.map((b) => ((b.id || b._id) === id ? { ...b, ...updated } : b))
      );
    } catch (err) {
      setError('Could not update email status: ' + err.message);
    }
  };

  const handleAddBrandSubmit = async (e) => {
    e.preventDefault();
    if (!newBrandForm.brand_name.trim() || !newBrandForm.storefront_domain.trim()) {
      setError('Brand name and storefront domain are required');
      return;
    }
    if (newBrandForm.return_days === '' || newBrandForm.shipping_days === '') {
      setError('Return Days and Shipping Days are required');
      return;
    }

    try {
      setError('');
      setMessage('');
      const payload = {
        ...newBrandForm,
        return_days: Number(newBrandForm.return_days),
        shipping_days: Number(newBrandForm.shipping_days),
        custom_fields: {
          ...(newBrandForm.custom_fields || {}),
          founder_name: newBrandForm.founder_name,
          founder_phone: newBrandForm.founder_phone,
          founder_email: newBrandForm.founder_email,
          founder_social: newBrandForm.founder_social,
          return_days: Number(newBrandForm.return_days),
          shipping_days: Number(newBrandForm.shipping_days),
        },
      };
      await adminApi.createBrand(payload);
      setMessage('Brand added successfully!');
      setAddModalOpen(false);
      setNewBrandForm({
        brand_name: '',
        storefront_domain: '',
        founder_name: '',
        founder_phone: '',
        founder_email: '',
        founder_social: '',
        return_days: '',
        shipping_days: '',
        email_sent: false,
        custom_fields: {},
      });
      fetchBrands();
      fetchCustomFields();
    } catch (err) {
      setError(err.message || 'Failed to create brand');
    }
  };

  const handleRegisterFieldSubmit = async (e) => {
    e.preventDefault();
    const key = newFieldKey.trim().toLowerCase();
    if (!key) return;

    try {
      setError('');
      setMessage('');
      const res = await adminApi.registerCustomField(key);
      setMessage(`Custom field "${key}" registered!`);
      setNewFieldKey('');
      setFieldModalOpen(false);
      setCustomFieldKeys(res.keys || [...customFieldKeys, key]);
    } catch (err) {
      setError(err.message || 'Failed to register custom field');
    }
  };

  const openEditModal = (brand) => {
    setSelectedBrand(brand);
    const custom = brand.custom_fields || {};
    const retDays = getReturnDays(brand);
    const shipDays = getShippingDays(brand);
    const cancelVal = getCancellationAllowed(brand);

    const fName = brand.founder_name || brand.founder || brand.founders || custom.founder_name || custom.founder || '';
    const fPhone = brand.founder_phone || brand.founderPhone || brand.phone || custom.founder_phone || custom.founderPhone || custom.phone || '';
    const fEmail = brand.founder_email || brand.founderEmail || custom.founder_email || custom.founderEmail || '';
    const fSocial = brand.founder_social || brand.founderSocial || brand.founder_linkedin || brand.founder_instagram || custom.founder_social || custom.founderSocial || custom.founder_linkedin || custom.founder_instagram || '';

    setEditBrandForm({
      brand_name: brand.brand_name || '',
      storefront_domain: brand.storefront_domain || '',
      founder_name: fName,
      founder_phone: fPhone,
      founder_email: fEmail,
      founder_social: fSocial,
      email_sent: Boolean(brand.email_sent),
      return_days: retDays !== '—' ? retDays : '',
      shipping_days: shipDays !== '—' ? shipDays : '',
      cancellation_allowed: cancelVal === true ? true : cancelVal === false ? false : '',
      custom_fields: {
        ...custom,
        founder_name: fName,
        founder_phone: fPhone,
        founder_email: fEmail,
        founder_social: fSocial,
        return_days: retDays !== '—' ? retDays : (custom.return_days ?? brand.return_days ?? ''),
        shipping_days: shipDays !== '—' ? shipDays : (custom.shipping_days ?? brand.shipping_days ?? ''),
        cancellation_allowed: cancelVal === true ? true : cancelVal === false ? false : (custom.cancellation_allowed ?? brand.cancellation_allowed ?? ''),
      },
    });
    setError('');
    setMessage('');
    setEditModalOpen(true);
  };

  const handleEditBrandSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBrand) return;

    try {
      setError('');
      setMessage('');
      const id = selectedBrand.id || selectedBrand._id;
      const retDaysVal = editBrandForm.return_days !== '' && editBrandForm.return_days != null ? Number(editBrandForm.return_days) : (editBrandForm.custom_fields?.return_days !== '' && editBrandForm.custom_fields?.return_days != null ? Number(editBrandForm.custom_fields.return_days) : null);
      const shipDaysVal = editBrandForm.shipping_days !== '' && editBrandForm.shipping_days != null ? Number(editBrandForm.shipping_days) : (editBrandForm.custom_fields?.shipping_days !== '' && editBrandForm.custom_fields?.shipping_days != null ? Number(editBrandForm.custom_fields.shipping_days) : null);
      const cancelVal = editBrandForm.cancellation_allowed === true || editBrandForm.cancellation_allowed === 'true'
        ? true
        : editBrandForm.cancellation_allowed === false || editBrandForm.cancellation_allowed === 'false'
          ? false
          : null;

      const payload = {
        ...editBrandForm,
        return_days: retDaysVal,
        shipping_days: shipDaysVal,
        cancellation_allowed: cancelVal,
        custom_fields: {
          ...(editBrandForm.custom_fields || {}),
          founder_name: editBrandForm.founder_name,
          founder_phone: editBrandForm.founder_phone,
          founder_email: editBrandForm.founder_email,
          founder_social: editBrandForm.founder_social,
          return_days: retDaysVal,
          shipping_days: shipDaysVal,
          cancellation_allowed: cancelVal,
        },
      };

      const updated = await adminApi.updateBrand(id, payload);
      setMessage('Brand updated successfully!');
      setEditModalOpen(false);
      setSelectedBrand(null);
      setBrands((prev) =>
        prev.map((b) => ((b.id || b._id) === id ? { ...b, ...updated } : b))
      );
    } catch (err) {
      setError(err.message || 'Failed to update brand');
    }
  };


  const handleToggleBrandActive = async (brand, e) => {
    e.stopPropagation();
    const id = brand.id || brand._id;
    const isCurrentlyActive = brand.is_active !== false;
    const nextActive = !isCurrentlyActive;
    const brandName = brand.brand_name || 'this brand';

    const promptMessage = isCurrentlyActive
      ? `Are you sure you want to DISABLE "${brandName}"?\n\nThis will temporarily move all products of this brand to the "products_archived" collection and mark the brand as inactive in brands_info.`
      : `Are you sure you want to ENABLE "${brandName}"?\n\nThis will restore all products from the "products_archived" collection back to "products" (with archive traces cleanly removed) and reactivate the brand in brands_info.`;

    if (!window.confirm(promptMessage)) return;

    try {
      setTogglingBrandId(id);
      setError('');
      setMessage('');
      const res = await adminApi.toggleBrandActive(id, nextActive);
      const updatedBrand = res.brand || { ...brand, is_active: nextActive };

      setBrands((prev) =>
        prev.map((b) => ((b.id || b._id) === id ? { ...b, ...updatedBrand } : b))
      );
      setMessage(
        `Brand "${brandName}" has been ${nextActive ? 'enabled' : 'disabled'} successfully. (${res.products_moved || 0} products moved)`
      );
    } catch (err) {
      console.error('Error toggling brand active state:', err);
      setError(err.message || 'Failed to update brand status');
    } finally {
      setTogglingBrandId(null);
    }
  };

  // Helper to safely format cell values as strings (never react elements/objects)
  const renderCellValue = useCallback((val) => {
    if (val == null || val === '' || val === '—') return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return '—';
      }
    }
    return String(val);
  }, []);

  // Extra custom fields (excluding standard fields, hidden fields, and object fields like custom_fields)
  const extraCustomFields = useMemo(() => {
    const hiddenFields = ['address', 'email', 'notes', 'note', 'email_sent', 'founder_address', 'founder_email'];
    const standardFields = ['return_days', 'shipping_days', 'cancellation_allowed', 'founder', 'founder_name', 'founder_phone', 'founder_email', 'founder_social', 'custom_fields', 'custom', 'email_sent', 'live_on_app', 'is_live', 'imported'];
    return customFieldKeys.filter((k) => {
      const lower = k.toLowerCase().trim();
      if (standardFields.includes(k) || hiddenFields.includes(lower)) return false;
      if (k === 'products' || k === 'products_active_count' || k.startsWith('products_shopify_')) return false;
      return true;
    });
  }, [customFieldKeys]);

  const filterOptionLabel = useCallback((key) => {
    if (key === 'live_on_app' || key === 'is_live' || key === 'imported') return 'Live on App';
    if (key === 'shipping_days') return 'Shipping Days';
    if (key === 'cancellation_allowed') return 'Cancellation';
    if (key === 'return_days') return 'Return Days';
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  // Defined table columns list
  const columns = useMemo(() => {
    const cols = [
      { key: 'brand_name', label: 'Brand Name', stickyClass: 'col-sticky-1' },
      { key: 'storefront_domain', label: 'Storefront Domain', stickyClass: 'col-sticky-2' },
      { key: 'products', label: 'Products', stickyClass: 'col-sticky-3' },
      { key: 'live_on_app', label: 'Live on App' },
      { key: 'founder', label: 'Founder' },
      { key: 'return_days', label: 'Return Days' },
      { key: 'shipping_days', label: 'Shipping Days' },
      { key: 'cancellation_allowed', label: 'Cancellation' },
    ];
    extraCustomFields.forEach((k) => {
      cols.push({ key: k, label: filterOptionLabel(k) });
    });
    // Second-to-last column before Actions
    cols.push({ key: 'notes', label: 'Notes' });
    return cols;
  }, [extraCustomFields, filterOptionLabel]);

  const getBrandNote = useCallback((brand) => {
    if (!brand) return '';
    const custom = brand.custom_fields || {};
    return (brand.notes || brand.note || custom.notes || custom.note || '').trim();
  }, []);

  const getHasNote = useCallback((brand) => {
    const note = getBrandNote(brand);
    return Boolean(note && note.length > 0);
  }, [getBrandNote]);

  const getIsLiveOnApp = useCallback((brand) => {
    if (!brand) return false;
    if (brand.live_on_app === true || brand.is_live === true || brand.imported === true) return true;
    if (brand.live_on_app === false || brand.is_live === false || brand.imported === false) return false;
    const custom = brand.custom_fields || {};
    if (custom.live_on_app === true || custom.is_live === true || custom.imported === true) return true;
    if (custom.live_on_app === false || custom.is_live === false || custom.imported === false) return false;

    const count =
      brand.products_active_count ??
      brand.products ??
      brand.products_shopify_imported_count ??
      custom.products_active_count ??
      custom.products;

    if (count != null && count !== '' && count !== '—') {
      const num = Number(count);
      return !isNaN(num) && num > 0;
    }
    return false;
  }, []);

  const getReturnDays = useCallback((brand) => {
    if (!brand) return '—';
    const custom = brand.custom_fields || {};
    const val =
      custom.return_days ??
      custom.returnDays ??
      custom.return_window ??
      custom.returns_days ??
      brand.return_days ??
      brand.returnDays ??
      brand.return_window ??
      brand.returns_days ??
      brand.return_policy_days;

    if (val != null && val !== '' && !Number.isNaN(Number(val))) {
      return Number(val);
    }
    if (val != null && val !== '') return val;
    return '—';
  }, []);

  const getShippingDays = useCallback((brand) => {
    if (!brand) return '—';
    const custom = brand.custom_fields || {};
    const val =
      custom.shipping_days ??
      custom.shippingDays ??
      custom.shipping_lead_time ??
      custom.delivery_days ??
      brand.shipping_days ??
      brand.shippingDays ??
      brand.shipping_lead_time ??
      brand.delivery_days ??
      brand.shipping_time;

    if (val != null && val !== '' && !Number.isNaN(Number(val))) {
      return Number(val);
    }
    if (val != null && val !== '') return val;
    return '—';
  }, []);

  const getCancellationAllowed = useCallback((brand) => {
    if (!brand) return '—';
    const custom = brand.custom_fields || {};
    const val =
      custom.cancellation_allowed ??
      custom.cancellationAllowed ??
      brand.cancellation_allowed ??
      brand.cancellationAllowed;

    if (val === true || val === 'true' || val === 'yes' || val === 1) return true;
    if (val === false || val === 'false' || val === 'no' || val === 0) return false;
    return '—';
  }, []);

  const getColumnValue = useCallback(
    (brand, colKey) => {
      if (!brand) return '(Blanks)';
      const custom = brand.custom_fields || {};
      if (colKey === 'brand_name') {
        return brand.brand_name?.trim() || '(Blanks)';
      }
      if (colKey === 'storefront_domain') {
        return brand.storefront_domain?.trim() || '(Blanks)';
      }
      if (colKey === 'products') {
        const val =
          brand.products_active_count ??
          brand.products ??
          brand.products_shopify_imported_count ??
          custom.products_active_count ??
          custom.products;
        return val != null && val !== '' && val !== '—' ? String(val) : '(Blanks)';
      }
      if (colKey === 'live_on_app' || colKey === 'is_live' || colKey === 'imported') {
        return getIsLiveOnApp(brand) ? 'Yes' : 'No';
      }
      if (colKey === 'founder') {
        const fName = (brand.founder_name || brand.founder || brand.founders || custom.founder_name || custom.founder || '').trim();
        const fPhone = (brand.founder_phone || brand.founderPhone || brand.phone || custom.founder_phone || custom.founderPhone || custom.phone || '').trim();
        const fEmail = (brand.founder_email || brand.founderEmail || brand.email || custom.founder_email || custom.founderEmail || custom.email || '').trim();
        const fSocial = (brand.founder_social || brand.founderSocial || brand.social || custom.founder_social || custom.founderSocial || custom.social || '').trim();
        if (fName) return fName;
        if (fEmail) return fEmail;
        if (fPhone) return fPhone;
        if (fSocial) return fSocial;
        return '(Blanks)';
      }
      if (colKey === 'return_days') {
        const val = getReturnDays(brand);
        return val !== '—' && val != null && val !== '' ? String(val) : '(Blanks)';
      }
      if (colKey === 'shipping_days') {
        const val = getShippingDays(brand);
        return val !== '—' && val != null && val !== '' ? String(val) : '(Blanks)';
      }
      if (colKey === 'cancellation_allowed') {
        const val = getCancellationAllowed(brand);
        return val === true ? 'Yes' : val === false ? 'No' : '(Blanks)';
      }
      if (colKey === 'email_sent') {
        return brand.email_sent ? 'Sent' : 'Not Sent';
      }
      if (colKey === 'notes' || colKey === 'note') {
        return getHasNote(brand) ? 'Yes' : 'No';
      }
      const val = custom[colKey] ?? brand[colKey];
      if (val == null || val === '' || val === '—') return '(Blanks)';
      return renderCellValue(val);
    },
    [getIsLiveOnApp, getReturnDays, getShippingDays, getCancellationAllowed, getHasNote, renderCellValue]
  );

  const getUniqueValuesForColumn = useCallback(
    (colKey) => {
      const valuesSet = new Set();
      brands.forEach((b) => {
        valuesSet.add(getColumnValue(b, colKey));
      });
      const vals = Array.from(valuesSet);
      vals.sort((a, b) => {
        if (a === '(Blanks)') return 1;
        if (b === '(Blanks)') return -1;
        const numA = Number(a);
        const numB = Number(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      });
      return vals;
    },
    [brands, getColumnValue]
  );

  const handleOpenFilter = (colKey, e) => {
    e.stopPropagation();
    if (activeFilterCol === colKey) {
      setActiveFilterCol(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    let left = rect.left;
    if (left + 260 > window.innerWidth - 16) {
      left = window.innerWidth - 276;
    }
    setPopoverPos({
      top: rect.bottom + 6,
      left: Math.max(16, left),
    });
    setValSearch('');
    setActiveFilterCol(colKey);
  };

  const isValueSelected = useCallback(
    (colKey, val) => {
      const selected = columnFilters[colKey];
      if (!selected) return true;
      return selected.includes(val);
    },
    [columnFilters]
  );

  const handleToggleValue = useCallback(
    (colKey, val, allVals) => {
      const currentSelected = columnFilters[colKey] ? [...columnFilters[colKey]] : [...allVals];
      let nextSelected;
      if (currentSelected.includes(val)) {
        nextSelected = currentSelected.filter((v) => v !== val);
      } else {
        nextSelected = [...currentSelected, val];
      }

      if (nextSelected.length === allVals.length) {
        const nextFilters = { ...columnFilters };
        delete nextFilters[colKey];
        setColumnFilters(nextFilters);
      } else {
        setColumnFilters({ ...columnFilters, [colKey]: nextSelected });
      }
    },
    [columnFilters]
  );

  const handleSelectAll = useCallback(
    (colKey, visibleVals, allVals) => {
      if (visibleVals.length === allVals.length) {
        const nextFilters = { ...columnFilters };
        delete nextFilters[colKey];
        setColumnFilters(nextFilters);
      } else {
        const currentSelected = columnFilters[colKey] ? [...columnFilters[colKey]] : [];
        const set = new Set([...currentSelected, ...visibleVals]);
        const nextSelected = Array.from(set);
        if (nextSelected.length >= allVals.length) {
          const nextFilters = { ...columnFilters };
          delete nextFilters[colKey];
          setColumnFilters(nextFilters);
        } else {
          setColumnFilters({ ...columnFilters, [colKey]: nextSelected });
        }
      }
    },
    [columnFilters]
  );

  const handleDeselectAll = useCallback(
    (colKey, visibleVals, allVals) => {
      if (visibleVals.length === allVals.length) {
        setColumnFilters({ ...columnFilters, [colKey]: [] });
      } else {
        const currentSelected = columnFilters[colKey] ? [...columnFilters[colKey]] : [...allVals];
        const visibleSet = new Set(visibleVals);
        const nextSelected = currentSelected.filter((v) => !visibleSet.has(v));
        setColumnFilters({ ...columnFilters, [colKey]: nextSelected });
      }
    },
    [columnFilters]
  );

  const handleClearColFilter = useCallback(
    (colKey) => {
      const nextFilters = { ...columnFilters };
      delete nextFilters[colKey];
      setColumnFilters(nextFilters);
    },
    [columnFilters]
  );

  const handleSort = useCallback(
    (colKey, direction) => {
      if (sortConfig.key === colKey && sortConfig.direction === direction) {
        setSortConfig({ key: null, direction: 'asc' });
      } else {
        setSortConfig({ key: colKey, direction });
      }
    },
    [sortConfig]
  );

  const handleRangeChange = useCallback((colKey, field, val) => {
    setRangeFilters((prev) => ({
      ...prev,
      [colKey]: {
        ...(prev[colKey] || { min: '', max: '' }),
        [field]: val,
      },
    }));
  }, []);

  const handleClearRange = useCallback((colKey) => {
    setRangeFilters((prev) => ({
      ...prev,
      [colKey]: { min: '', max: '' },
    }));
  }, []);

  const handleResetAllFilters = useCallback(() => {
    setColumnFilters({});
    setRangeFilters({
      products: { min: '', max: '' },
      return_days: { min: '', max: '' },
      shipping_days: { min: '', max: '' },
    });
    setSortConfig({ key: null, direction: 'asc' });
  }, []);

  const renderFounderCell = useCallback((b) => {
    const custom = b.custom_fields || {};
    let name = (b.founder_name || b.founder || b.founders || custom.founder_name || custom.founder || '').trim();
    let phone = (b.founder_phone || b.founderPhone || b.phone || custom.founder_phone || custom.founderPhone || custom.phone || '').trim();
    let email = (b.founder_email || b.founderEmail || b.email || custom.founder_email || custom.founderEmail || custom.email || '').trim();

    let linkedinUrl = (b.founder_linkedin || b.linkedin || custom.founder_linkedin || custom.linkedin || '').trim();
    let instagramUrl = (b.founder_instagram || b.instagram || custom.founder_instagram || custom.instagram || '').trim();
    const socialRaw = (b.founder_social || b.founderSocial || b.social || custom.founder_social || custom.founderSocial || custom.social || '').trim();

    if (name) {
      const lowerName = name.toLowerCase();
      if (lowerName.includes('linkedin')) {
        if (!linkedinUrl) linkedinUrl = name;
        name = '';
      } else if (lowerName.includes('instagram') || lowerName.includes('instagr.am') || name.startsWith('@')) {
        if (!instagramUrl) instagramUrl = name;
        name = '';
      } else if (name.startsWith('http://') || name.startsWith('https://') || name.startsWith('www.')) {
        if (!instagramUrl && !linkedinUrl) instagramUrl = name;
        name = '';
      } else if (name.includes('@') && name.includes('.') && !email) {
        email = name;
        name = '';
      } else if (/^[\d\s+\-()]{7,}$/.test(name) && !phone) {
        phone = name;
        name = '';
      }
    }

    if (socialRaw) {
      const lower = socialRaw.toLowerCase();
      if (lower.includes('linkedin') && !linkedinUrl) {
        linkedinUrl = socialRaw;
      } else if ((lower.includes('instagram') || lower.includes('instagr.am') || lower.startsWith('@')) && !instagramUrl) {
        instagramUrl = socialRaw;
      } else if (!linkedinUrl && !instagramUrl) {
        instagramUrl = socialRaw;
      }
    }

    if (!name && !phone && !email && !linkedinUrl && !instagramUrl) {
      return '—';
    }

    let formattedLinkedin = linkedinUrl ? linkedinUrl.trim() : '';
    if (formattedLinkedin) {
      if (!formattedLinkedin.startsWith('http://') && !formattedLinkedin.startsWith('https://')) {
        if (formattedLinkedin.includes('linkedin.com')) {
          formattedLinkedin = `https://${formattedLinkedin}`;
        } else {
          formattedLinkedin = `https://linkedin.com/in/${formattedLinkedin}`;
        }
      }
    }

    let formattedInstagram = instagramUrl ? instagramUrl.trim() : '';
    if (formattedInstagram) {
      if (formattedInstagram.startsWith('@')) {
        formattedInstagram = `https://instagram.com/${formattedInstagram.slice(1)}`;
      } else if (!formattedInstagram.startsWith('http://') && !formattedInstagram.startsWith('https://')) {
        if (formattedInstagram.includes('instagram.com') || formattedInstagram.includes('instagr.am')) {
          formattedInstagram = `https://${formattedInstagram}`;
        } else {
          formattedInstagram = `https://instagram.com/${formattedInstagram}`;
        }
      }
    }

    const cleanPhone = phone ? phone.replace(/[^\d+]/g, '') : '';
    const phoneUrl = cleanPhone ? `tel:${cleanPhone}` : `tel:${phone}`;

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {name && <span>{name}</span>}

        {formattedLinkedin && (
          <a
            href={formattedLinkedin}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0a66c2', display: 'inline-flex', alignItems: 'center' }}
            title={`LinkedIn: ${linkedinUrl}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
            </svg>
          </a>
        )}

        {formattedInstagram && (
          <a
            href={formattedInstagram}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#e1306c', display: 'inline-flex', alignItems: 'center' }}
            title={`Instagram: ${instagramUrl}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </a>
        )}

        {email && (
          <a
            href={`mailto:${email}`}
            style={{ color: '#0284c7', display: 'inline-flex', alignItems: 'center' }}
            title={`Email: ${email}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </a>
        )}

        {phone && (
          <a
            href={phoneUrl}
            style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center' }}
            title={`Phone: ${phone}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
          </a>
        )}
      </div>
    );
  }, []);

  // Filtered & sorted brands based on tab, column filters, range filters, sorting, and search query
  const filteredBrands = useMemo(() => {
    let result = brands.filter((b) => {
      // 1. Tab filter
      if (activeTab === 'partner' && !b.partnership) return false;
      if (activeTab === 'candidates' && b.partnership) return false;

      // 2. Excel Column value checklist filters
      for (const [colKey, selectedVals] of Object.entries(columnFilters)) {
        if (selectedVals && Array.isArray(selectedVals)) {
          const val = getColumnValue(b, colKey);
          if (!selectedVals.includes(val)) return false;
        }
      }

      // 3. Numeric Min-Max Range filters (for products, return_days, shipping_days)
      for (const [colKey, range] of Object.entries(rangeFilters)) {
        if (range && (range.min !== '' || range.max !== '')) {
          const rawVal = getColumnValue(b, colKey);
          if (rawVal === '(Blanks)' || rawVal === '—') return false;
          const numVal = Number(rawVal);
          if (isNaN(numVal)) return false;
          if (range.min !== '' && numVal < Number(range.min)) return false;
          if (range.max !== '' && numVal > Number(range.max)) return false;
        }
      }

      // 4. Search query filter
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const nameMatch = (b.brand_name || '').toLowerCase().includes(q);
        const domainMatch = (b.storefront_domain || '').toLowerCase().includes(q);
        const custom = b.custom_fields || {};
        const fName = (b.founder || b.founder_name || b.founders || custom.founder || custom.founder_name || '').toLowerCase();
        const fPhone = (b.founder_phone || b.founderPhone || b.phone || custom.founder_phone || custom.founderPhone || custom.phone || '').toLowerCase();
        const fEmail = (b.founder_email || b.founderEmail || custom.founder_email || custom.founderEmail || '').toLowerCase();
        const fSocial = (b.founder_social || b.founderSocial || b.founder_linkedin || b.founder_instagram || custom.founder_social || custom.founderSocial || custom.founder_linkedin || custom.founder_instagram || '').toLowerCase();
        const founderMatch = fName.includes(q) || fPhone.includes(q) || fEmail.includes(q) || fSocial.includes(q);

        let cfMatch = false;
        if (b.custom_fields) {
          for (const v of Object.values(b.custom_fields)) {
            if (v != null && String(v).toLowerCase().includes(q)) {
              cfMatch = true;
              break;
            }
          }
        }
        if (!nameMatch && !domainMatch && !founderMatch && !cfMatch) return false;
      }

      return true;
    });

    // 5. Sorting (Alpha A-Z / Z-A or numeric sort)
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        const valA = getColumnValue(a, sortConfig.key);
        const valB = getColumnValue(b, sortConfig.key);

        if (valA === '(Blanks)') return 1;
        if (valB === '(Blanks)') return -1;

        const numA = Number(valA);
        const numB = Number(valB);

        let cmp = 0;
        if (!isNaN(numA) && !isNaN(numB)) {
          cmp = numA - numB;
        } else {
          cmp = String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base' });
        }

        return sortConfig.direction === 'desc' ? -cmp : cmp;
      });
    }

    return result;
  }, [brands, activeTab, columnFilters, rangeFilters, sortConfig, getColumnValue, search]);

  // Statistics
  const totalBrandsCount = brands.length;
  const showingBrandsCount = filteredBrands.length;
  const showingPct = totalBrandsCount ? ((showingBrandsCount / totalBrandsCount) * 100).toFixed(1) : '0.0';

  const totalProductsCount = useMemo(() => {
    return filteredBrands.reduce((sum, b) => {
      const count =
        b.products_active_count ??
        b.products ??
        b.products_shopify_imported_count ??
        (b.custom_fields && (b.custom_fields.products_active_count ?? b.custom_fields.products));
      if (count == null || count === '' || count === '—') return sum;
      const n = Number(count);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [filteredBrands]);

  // Download CSV report
  const handleDownloadCSV = () => {
    if (!filteredBrands.length) {
      alert('No brands to download');
      return;
    }
    const headers = ['brand_name', 'storefront_domain', 'products', 'live_on_app', 'founder_name', 'founder_phone', 'founder_email', 'founder_social', 'partnership', 'return_days', 'shipping_days', 'cancellation_allowed', 'notes', 'email_sent'];
    const csvEscape = (val) => {
      const s = val == null ? '' : String(val);
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [headers.map(csvEscape).join(',')];
    filteredBrands.forEach((b) => {
      const custom = b.custom_fields || {};
      const productsCount =
        b.products_active_count ??
        b.products ??
        b.products_shopify_imported_count ??
        custom.products_active_count ??
        custom.products ??
        '';
      const liveOnApp = getIsLiveOnApp(b) ? 'Yes' : 'No';
      const fName = b.founder || b.founder_name || b.founders || custom.founder || custom.founder_name || '';
      const fPhone = b.founder_phone || b.founderPhone || b.phone || custom.founder_phone || custom.founderPhone || custom.phone || '';
      const fEmail = b.founder_email || b.founderEmail || custom.founder_email || custom.founderEmail || '';
      const fSocial = b.founder_social || b.founderSocial || b.founder_linkedin || b.founder_instagram || custom.founder_social || custom.founderSocial || custom.founder_linkedin || custom.founder_instagram || '';
      const returnDays = getReturnDays(b) === '—' ? '' : getReturnDays(b);
      const shippingDays = getShippingDays(b) === '—' ? '' : getShippingDays(b);
      const cancelVal = getCancellationAllowed(b);
      const cancelAllowed = cancelVal === true ? 'Yes' : cancelVal === false ? 'No' : '';
      const brandNote = getBrandNote(b);

      const row = [
        b.brand_name || '',
        b.storefront_domain || '',
        productsCount,
        liveOnApp,
        fName,
        fPhone,
        fEmail,
        fSocial,
        b.partnership ? 'partner' : 'outreach',
        returnDays,
        shippingDays,
        cancelAllowed,
        brandNote,
        b.email_sent ? 'sent' : 'not sent',
      ];
      lines.push(row.map(csvEscape).join(','));
    });

    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `brands_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="brands-onboarding-root">
      <TopMenu pageTitle="Brands Onboarding" email={email} onLogout={onLogout} />

      <div className="brands-main">
        <div className="brands-header">
          <div>
            <h1>Brand Onboarding Manager</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>
              Track seller onboarding, domain verification, custom policies, and Shopify sync status
            </p>
          </div>
          <div className="brands-header-actions">
            <button className="brands-btn-secondary" onClick={() => setFieldModalOpen(true)}>
              + New Custom Field
            </button>
            <button className="brands-btn-primary" onClick={() => setAddModalOpen(true)}>
              + Add Brand
            </button>
          </div>
        </div>

        {error && (
          <div className="brands-alert error">
            <span>{error}</span>
          </div>
        )}
        {message && <div className="brands-alert success">{message}</div>}

        <div className="brands-tabs">
          <button
            type="button"
            className={`brands-tab-btn ${activeTab === 'partner' ? 'active' : ''}`}
            onClick={() => setActiveTab('partner')}
          >
            ⚡ Active Shopify App Partners
          </button>
          <button
            type="button"
            className={`brands-tab-btn ${activeTab === 'candidates' ? 'active' : ''}`}
            onClick={() => setActiveTab('candidates')}
          >
            Outreach Candidates
          </button>
          <button
            type="button"
            className={`brands-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Brands
          </button>
        </div>

        <div className="brands-toolbar">
          <input
            type="search"
            className="brands-search-input"
            placeholder="Search by brand name, myshopify domain, or custom field..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* Reset All Column Filters button if active */}
          {(() => {
            const activeRangeCount = Object.values(rangeFilters).filter((r) => r.min !== '' || r.max !== '').length;
            const totalActiveCount = Object.keys(columnFilters).length + activeRangeCount + (sortConfig.key ? 1 : 0);
            if (totalActiveCount === 0) return null;
            return (
              <button
                type="button"
                className="brands-btn-secondary"
                style={{ borderColor: '#ef4444', color: '#dc2626', fontWeight: 600 }}
                onClick={handleResetAllFilters}
                title="Clear all active column filters, ranges, and sorting"
              >
                ✕ Reset Filters ({totalActiveCount})
              </button>
            );
          })()}

          {/* Download CSV Button */}
          <button
            type="button"
            className="brands-btn-secondary"
            onClick={handleDownloadCSV}
            title="Download CSV report of currently filtered brands"
          >
            ⬇ CSV
          </button>

          {/* Summary Stats in the same line */}
          <div className="brands-summary-stats">
            <span className="stat-pill">
              <span className="stat-label">Showing:</span>
              <strong className="stat-value">{showingBrandsCount}</strong>
              <span className="stat-sub">({showingPct}%)</span>
            </span>
            <span className="stat-divider">•</span>
            <span className="stat-pill">
              <span className="stat-label">Total Brands:</span>
              <strong className="stat-value">{totalBrandsCount}</strong>
            </span>
            <span className="stat-divider">•</span>
            <span className="stat-pill">
              <span className="stat-label">Total Products:</span>
              <strong className="stat-value accent">{totalProductsCount.toLocaleString('en-IN')}</strong>
            </span>
          </div>
        </div>

        <div className="brands-table-card">
          <div className="brands-table-scroll" onScroll={() => setActiveFilterCol(null)}>
            <table className="brands-table">
              <thead>
                <tr>
                  <th className="col-sticky-sr">#</th>
                  {columns.map((col) => {
                    const isRangeActive = Boolean(rangeFilters[col.key] && (rangeFilters[col.key].min !== '' || rangeFilters[col.key].max !== ''));
                    const isSortActive = sortConfig.key === col.key;
                    const isFiltered = Boolean(columnFilters[col.key]) || isRangeActive || isSortActive;

                    return (
                      <th key={col.key} className={col.stickyClass || ''}>
                        <div className="th-content">
                          <span>
                            {col.label}
                            {isSortActive && (
                              <span style={{ marginLeft: '4px', fontSize: '11px', color: '#0284c7' }}>
                                {sortConfig.direction === 'asc' ? '▲' : '▼'}
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            className={`th-filter-btn ${isFiltered ? 'active' : ''}`}
                            onClick={(e) => handleOpenFilter(col.key, e)}
                            title={`Filter / Sort ${col.label}`}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill={isFiltered ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                          </button>
                        </div>
                      </th>
                    );
                  })}
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && brands.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      Loading brands...
                    </td>
                  </tr>
                ) : filteredBrands.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      No brands found matching criteria.
                    </td>
                  </tr>
                ) : (
                  filteredBrands.map((b, index) => {
                    const id = b.id || b._id;
                    const custom = b.custom_fields || {};
                    const productsCount =
                      b.products_active_count ??
                      b.products ??
                      b.products_shopify_imported_count ??
                      (custom.products_active_count ?? custom.products) ??
                      '—';
                    const discCount = b.products_shopify_daily_discontinued_missing;
                    const returnDays = getReturnDays(b);
                    const shippingDays = getShippingDays(b);
                    const cancelAllowed = getCancellationAllowed(b);

                    return (
                      <tr key={id}>
                        <td className="col-sticky-sr">
                          {index + 1}
                        </td>
                        <td className="col-sticky-1" style={{ fontWeight: 600, color: '#0f172a' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flexWrap: 'nowrap' }}>
                            <span>{b.brand_name}</span>
                            <Link
                              to={`/brands/${id}/gallery`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="brand-gallery-icon-btn"
                              title={`Open ${b.brand_name} Products Gallery`}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                padding: '3px 5px',
                                color: '#0284c7',
                                textDecoration: 'none',
                                transition: 'all 0.15s ease',
                                flexShrink: 0,
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                            </Link>
                          </div>
                          {b.is_active === false && <span className="brand-disabled-badge">Disabled</span>}
                          {b.partnership && <span className="partner-badge">⚡ Partner</span>}
                        </td>
                        <td className="col-sticky-2">
                          <a
                            href={`https://${b.storefront_domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="brands-domain-link"
                          >
                            {b.storefront_domain}
                          </a>
                        </td>
                        <td className="col-sticky-3">
                          <span style={{ color: '#0284c7', fontWeight: 600 }}>{productsCount}</span>
                          {discCount > 0 && (
                            <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }} title={`${discCount} discontinued products`}>
                              ({discCount} disc.)
                            </span>
                          )}
                          {b.products_shopify_imported_truncated && (
                            <span style={{ fontSize: '11px', color: '#d97706', marginLeft: '4px' }}>
                              (truncated)
                            </span>
                          )}
                        </td>
                        <td>
                          {getIsLiveOnApp(b) ? (
                            <span style={{ color: '#16a34a', fontWeight: 600 }}>Yes</span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>No</span>
                          )}
                        </td>
                        <td>{renderFounderCell(b)}</td>
                        <td>{returnDays}</td>
                        <td>{shippingDays}</td>
                        <td>
                          {cancelAllowed === true ? (
                            <span style={{ color: '#16a34a' }}>Yes</span>
                          ) : cancelAllowed === false ? (
                            <span style={{ color: '#dc2626' }}>No</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        {extraCustomFields.map((k) => (
                          <td key={k}>{renderCellValue(custom[k] ?? b[k])}</td>
                        ))}
                        <td>
                          {getHasNote(b) ? (
                            <span
                              style={{
                                color: '#16a34a',
                                fontWeight: 600,
                                cursor: 'help',
                                borderBottom: '1px dotted #16a34a',
                              }}
                              title={getBrandNote(b)}
                            >
                              Yes
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>No</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '8px' }}>
                            <button
                              className={b.is_active === false ? 'brands-btn-success' : 'brands-btn-warning'}
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                              disabled={togglingBrandId === id}
                              onClick={(e) => handleToggleBrandActive(b, e)}
                              title={b.is_active === false ? 'Enable brand and restore products' : 'Disable brand and archive products'}
                            >
                              {togglingBrandId === id ? '...' : (b.is_active === false ? 'Enable' : 'Disable')}
                            </button>
                            <button
                              className="brands-btn-secondary"
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                              onClick={() => openEditModal(b)}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Excel Filter Popover */}
        {activeFilterCol && (() => {
          const colObj = columns.find((c) => c.key === activeFilterCol);
          const allVals = getUniqueValuesForColumn(activeFilterCol);
          const visibleVals = valSearch.trim()
            ? allVals.filter((v) => v.toLowerCase().includes(valSearch.trim().toLowerCase()))
            : allVals;

          const isNumericCol = ['products', 'return_days', 'shipping_days'].includes(activeFilterCol);
          const currentRange = rangeFilters[activeFilterCol] || { min: '', max: '' };
          const hasRangeFilter = currentRange.min !== '' || currentRange.max !== '';
          const isSorted = sortConfig.key === activeFilterCol;
          const isValFiltered = Boolean(columnFilters[activeFilterCol]);
          const isFiltered = isValFiltered || hasRangeFilter || isSorted;

          return (
            <div
              className="excel-filter-popover"
              style={{ top: popoverPos.top, left: popoverPos.left }}
              ref={popoverRef}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="excel-filter-header">
                <span className="excel-filter-title">
                  Filter: {colObj?.label || activeFilterCol}
                </span>
                <button
                  type="button"
                  className="excel-filter-close"
                  onClick={() => setActiveFilterCol(null)}
                >
                  &times;
                </button>
              </div>

              {/* Sort Options */}
              <div className="excel-filter-sort-section">
                <button
                  type="button"
                  className={`excel-filter-sort-btn ${isSorted && sortConfig.direction === 'asc' ? 'active' : ''}`}
                  onClick={() => handleSort(activeFilterCol, 'asc')}
                >
                  <span>{isNumericCol ? 'Sort Smallest to Largest (Asc)' : 'Sort A → Z (Ascending)'}</span>
                </button>
                <button
                  type="button"
                  className={`excel-filter-sort-btn ${isSorted && sortConfig.direction === 'desc' ? 'active' : ''}`}
                  onClick={() => handleSort(activeFilterCol, 'desc')}
                >
                  <span>{isNumericCol ? 'Sort Largest to Smallest (Desc)' : 'Sort Z → A (Descending)'}</span>
                </button>
              </div>

              {/* Min-Max Range Filter for Numeric Columns */}
              {isNumericCol && (
                <div className="excel-filter-range-box">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="excel-filter-range-title">Min-Max Range:</span>
                    {hasRangeFilter && (
                      <button
                        type="button"
                        className="excel-filter-link-btn"
                        style={{ color: '#dc2626' }}
                        onClick={() => handleClearRange(activeFilterCol)}
                      >
                        Clear Range
                      </button>
                    )}
                  </div>
                  <div className="excel-filter-range-inputs">
                    <input
                      type="number"
                      className="excel-filter-range-input"
                      placeholder="Min"
                      value={currentRange.min}
                      onChange={(e) => handleRangeChange(activeFilterCol, 'min', e.target.value)}
                    />
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
                    <input
                      type="number"
                      className="excel-filter-range-input"
                      placeholder="Max"
                      value={currentRange.max}
                      onChange={(e) => handleRangeChange(activeFilterCol, 'max', e.target.value)}
                    />
                  </div>
                  <span style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>
                    Empty fields mean Show All
                  </span>
                </div>
              )}

              {/* Value Search */}
              <input
                type="text"
                className="excel-filter-search"
                placeholder="Search values..."
                value={valSearch}
                onChange={(e) => setValSearch(e.target.value)}
              />

              <div className="excel-filter-actions">
                <button
                  type="button"
                  className="excel-filter-link-btn"
                  onClick={() => handleSelectAll(activeFilterCol, visibleVals, allVals)}
                >
                  Select All
                </button>
                <span style={{ color: '#cbd5e1' }}>|</span>
                <button
                  type="button"
                  className="excel-filter-link-btn"
                  onClick={() => handleDeselectAll(activeFilterCol, visibleVals, allVals)}
                >
                  Clear All
                </button>
              </div>

              <div className="excel-filter-list">
                {visibleVals.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#94a3b8', padding: '12px', textAlign: 'center' }}>
                    No matching values
                  </div>
                ) : (
                  visibleVals.map((val) => {
                    const isChecked = isValueSelected(activeFilterCol, val);
                    return (
                      <label key={val} className="excel-filter-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleValue(activeFilterCol, val, allVals)}
                        />
                        <span
                          style={{
                            fontStyle: val === '(Blanks)' ? 'italic' : 'normal',
                            color: val === '(Blanks)' ? '#94a3b8' : '#1e293b',
                          }}
                        >
                          {val}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="excel-filter-footer">
                <button
                  type="button"
                  className="excel-filter-clear-col-btn"
                  onClick={() => {
                    handleClearColFilter(activeFilterCol);
                    if (isNumericCol) handleClearRange(activeFilterCol);
                    if (sortConfig.key === activeFilterCol) {
                      setSortConfig({ key: null, direction: 'asc' });
                    }
                  }}
                  disabled={!isFiltered}
                >
                  Clear Column Filter
                </button>
                <button
                  type="button"
                  className="excel-filter-done-btn"
                  onClick={() => setActiveFilterCol(null)}
                >
                  Done
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Add Brand Modal */}
      {addModalOpen && (
        <div className="brands-modal-backdrop" onClick={() => setAddModalOpen(false)}>
          <div className="brands-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brands-modal-header">
              <h2>Add New Brand</h2>
              <button className="celeb-close-btn" onClick={() => setAddModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleAddBrandSubmit}>
              <div className="brands-modal-body">
                <div className="brands-form-group">
                  <label>Brand Name *</label>
                  <input
                    type="text"
                    className="brands-form-input"
                    placeholder="e.g. Zara, H&M"
                    value={newBrandForm.brand_name}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, brand_name: e.target.value })}
                    required
                  />
                </div>
                <div className="brands-form-group">
                  <label>Storefront Domain *</label>
                  <input
                    type="text"
                    className="brands-form-input"
                    placeholder="brandname.myshopify.com"
                    value={newBrandForm.storefront_domain}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, storefront_domain: e.target.value })}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                  <div className="brands-form-group">
                    <label>Founder Name</label>
                    <input
                      type="text"
                      className="brands-form-input"
                      placeholder="e.g. Founder Name"
                      value={newBrandForm.founder}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, founder: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Phone</label>
                    <input
                      type="tel"
                      className="brands-form-input"
                      placeholder="e.g. +1 234 567 890"
                      value={newBrandForm.founder_phone}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, founder_phone: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Email</label>
                    <input
                      type="email"
                      className="brands-form-input"
                      placeholder="founder@brand.com"
                      value={newBrandForm.founder_email}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, founder_email: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Social</label>
                    <input
                      type="text"
                      className="brands-form-input"
                      placeholder="Instagram / LinkedIn"
                      value={newBrandForm.founder_social}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, founder_social: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="brands-form-group">
                    <label>Return Days *</label>
                    <input
                      type="number"
                      className="brands-form-input"
                      placeholder="e.g. 7"
                      value={newBrandForm.return_days}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, return_days: e.target.value })}
                      required
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Shipping Days *</label>
                    <input
                      type="number"
                      className="brands-form-input"
                      placeholder="e.g. 3"
                      value={newBrandForm.shipping_days}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, shipping_days: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="brands-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newBrandForm.email_sent}
                      onChange={(e) => setNewBrandForm({ ...newBrandForm, email_sent: e.target.checked })}
                      style={{ width: 'auto', accentColor: '#0f172a' }}
                    />
                    <span>Onboarding email already sent</span>
                  </label>
                </div>
              </div>
              <div className="brands-modal-footer">
                <button type="button" className="brands-btn-secondary" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="brands-btn-primary">
                  Create Brand
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Register Custom Field Modal */}
      {fieldModalOpen && (
        <div className="brands-modal-backdrop" onClick={() => setFieldModalOpen(false)}>
          <div className="brands-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="brands-modal-header">
              <h2>Register Custom Field</h2>
              <button className="celeb-close-btn" onClick={() => setFieldModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleRegisterFieldSubmit}>
              <div className="brands-modal-body">
                <div className="brands-form-group">
                  <label>Field Key Name *</label>
                  <input
                    type="text"
                    className="brands-form-input"
                    placeholder="e.g. commission_rate, priority_tier"
                    value={newFieldKey}
                    onChange={(e) => setNewFieldKey(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Letters, numbers, and underscores only.
                  </span>
                </div>
              </div>
              <div className="brands-modal-footer">
                <button type="button" className="brands-btn-secondary" onClick={() => setFieldModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="brands-btn-primary">
                  Register Field
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Brand Modal */}
      {editModalOpen && selectedBrand && (
        <div className="brands-modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="brands-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brands-modal-header">
              <h2>Edit Brand: {selectedBrand.brand_name}</h2>
              <button className="celeb-close-btn" onClick={() => setEditModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditBrandSubmit}>
              <div className="brands-modal-body">
                <div className="brands-form-group">
                  <label>Brand Name</label>
                  <input
                    type="text"
                    className="brands-form-input"
                    value={editBrandForm.brand_name}
                    onChange={(e) => setEditBrandForm({ ...editBrandForm, brand_name: e.target.value })}
                    required
                  />
                </div>
                <div className="brands-form-group">
                  <label>Storefront Domain</label>
                  <input
                    type="text"
                    className="brands-form-input"
                    value={editBrandForm.storefront_domain}
                    onChange={(e) => setEditBrandForm({ ...editBrandForm, storefront_domain: e.target.value })}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                  <div className="brands-form-group">
                    <label>Founder Name</label>
                    <input
                      type="text"
                      className="brands-form-input"
                      placeholder="e.g. Founder Name"
                      value={editBrandForm.founder}
                      onChange={(e) => setEditBrandForm({ ...editBrandForm, founder: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Phone</label>
                    <input
                      type="text"
                      className="brands-form-input"
                      placeholder="e.g. +1 234 567 890"
                      value={editBrandForm.founder_phone}
                      onChange={(e) => setEditBrandForm({ ...editBrandForm, founder_phone: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Email</label>
                    <input
                      type="email"
                      className="brands-form-input"
                      placeholder="founder@brand.com"
                      value={editBrandForm.founder_email}
                      onChange={(e) => setEditBrandForm({ ...editBrandForm, founder_email: e.target.value })}
                    />
                  </div>
                  <div className="brands-form-group">
                    <label>Founder Social</label>
                    <input
                      type="text"
                      className="brands-form-input"
                      placeholder="Instagram / LinkedIn"
                      value={editBrandForm.founder_social}
                      onChange={(e) => setEditBrandForm({ ...editBrandForm, founder_social: e.target.value })}
                    />
                  </div>
                </div>
                <div className="brands-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editBrandForm.email_sent}
                      onChange={(e) => setEditBrandForm({ ...editBrandForm, email_sent: e.target.checked })}
                      style={{ width: 'auto', accentColor: '#0f172a' }}
                    />
                    <span>Onboarding email sent</span>
                  </label>
                </div>

                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#0f172a' }}>Custom Policy Fields</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div className="brands-form-group">
                      <label>Return Days</label>
                      <input
                        type="number"
                        className="brands-form-input"
                        value={editBrandForm.return_days ?? editBrandForm.custom_fields?.return_days ?? ''}
                        onChange={(e) =>
                          setEditBrandForm({
                            ...editBrandForm,
                            return_days: e.target.value,
                            custom_fields: { ...editBrandForm.custom_fields, return_days: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="brands-form-group">
                      <label>Shipping Days</label>
                      <input
                        type="number"
                        className="brands-form-input"
                        value={editBrandForm.shipping_days ?? editBrandForm.custom_fields?.shipping_days ?? ''}
                        onChange={(e) =>
                          setEditBrandForm({
                            ...editBrandForm,
                            shipping_days: e.target.value,
                            custom_fields: { ...editBrandForm.custom_fields, shipping_days: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="brands-form-group">
                      <label>Cancellation</label>
                      <select
                        className="brands-form-input"
                        value={
                          editBrandForm.cancellation_allowed === true || editBrandForm.cancellation_allowed === 'true'
                            ? 'true'
                            : editBrandForm.cancellation_allowed === false || editBrandForm.cancellation_allowed === 'false'
                              ? 'false'
                              : ''
                        }
                        onChange={(e) => {
                          const val = e.target.value === 'true' ? true : e.target.value === 'false' ? false : '';
                          setEditBrandForm({
                            ...editBrandForm,
                            cancellation_allowed: val,
                            custom_fields: { ...editBrandForm.custom_fields, cancellation_allowed: val },
                          });
                        }}
                      >
                        <option value="">Select policy...</option>
                        <option value="true">Yes (Allowed)</option>
                        <option value="false">No (Not Allowed)</option>
                      </select>
                    </div>
                  </div>

                  {extraCustomFields.map((field) => (
                    <div className="brands-form-group" key={field} style={{ marginTop: '12px' }}>
                      <label style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}</label>
                      <input
                        type="text"
                        className="brands-form-input"
                        value={editBrandForm.custom_fields[field] ?? ''}
                        onChange={(e) =>
                          setEditBrandForm({
                            ...editBrandForm,
                            custom_fields: { ...editBrandForm.custom_fields, [field]: e.target.value },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="brands-modal-footer">
                <button type="button" className="brands-btn-secondary" onClick={() => setEditModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="brands-btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandsOnboarding;
